/**
 * Skillsnap TypeScript Types
 * Requirements: Data Models from design.md
 */

// Job Phases
export type JobPhase =
  | 'Queued' | 'Generating' | 'Ready'
  | 'Applied' | 'Follow-Up' | 'Interviewing' | 'Declined' | 'Accepted'
  | 'Skipped' | 'Expired' | 'Errored' | 'Trash';

export const VALID_PHASES: JobPhase[] = [
  'Queued', 'Generating', 'Ready',
  'Applied', 'Follow-Up', 'Interviewing', 'Declined', 'Accepted',
  'Skipped', 'Expired', 'Errored', 'Trash'
];

export const ACTIVE_PHASES: JobPhase[] = [
  'Queued', 'Generating', 'Ready',
  'Applied', 'Follow-Up', 'Interviewing'
];

// Generation States
export type GenerationState = 'locked' | 'ready' | 'generating' | 'complete' | 'error' | 'retry';

// Generation Types
export type GenerationType = 'manual' | 'embedding' | 'generate' | 'omit';

// Subcomponent Types
export type SubcomponentType =
  | 'contact' | 'summary' | 'skills' | 'highlights'
  | 'experience' | 'education' | 'awards' | 'presentations'
  | 'publications' | 'portfolios' | 'coverletter' | 'elevatorpitch';

export const SUBCOMPONENTS: SubcomponentType[] = [
  'contact', 'summary', 'highlights', 'skills',
  'experience', 'education', 'awards', 'presentations',
  'publications', 'portfolios', 'coverletter', 'elevatorpitch'
];

// Default section order for resume rendering (excludes coverletter which is separate)
export const DEFAULT_SECTION_ORDER: SubcomponentType[] = [
  'contact', 'summary', 'highlights', 'skills',
  'experience', 'education', 'awards', 'presentations',
  'publications', 'portfolios'
];

// Generation type restrictions per component
// Components not listed here allow all types: manual, embedding, generate, omit
export const GENERATION_TYPE_RESTRICTIONS: Partial<Record<SubcomponentType, GenerationType[]>> = {
  contact: ['manual', 'omit'],
  summary: ['generate', 'manual', 'omit'],
  skills: ['embedding', 'manual', 'omit'],
  highlights: ['generate', 'embedding', 'omit'],
  experience: ['generate', 'embedding', 'manual', 'omit'],
  education: ['embedding', 'manual', 'omit'],
  awards: ['embedding', 'manual', 'omit'],
  presentations: ['embedding', 'manual', 'omit'],
  publications: ['embedding', 'manual', 'omit'],
  portfolios: ['embedding', 'manual', 'omit'],
  coverletter: ['generate', 'omit'],
  elevatorpitch: ['generate', 'omit'],
};

// Contact Item (for list of contact methods)
export interface ContactItem {
  icon: 'email-at' | 'phone' | 'phone-volume' | 'linkedin' | 'x-twitter' | 'bluesky' | 'github' | 'github-square' | 'globe' | 'house' | 'facebook' | 'instagram' | 'tiktok' | 'youtube' | 'discord' | 'slack' | 'telegram' | 'whatsapp' | 'signal-chat' | 'skillsnap';
  title: string;
  url: string;
  include?: string;
}

// Skill object (was bare string)
export interface Skill {
  title: string;
  tags: string[];
  include?: string;
  // Links the skill to a SkillGroup.id. null/undefined = ungrouped (auto-bucketed on save).
  group_id?: string | null;
}

// Skill Group — a user-curated cluster of skills with a stable PK. At generation
// time, groups are selected per-job and labeled with the closest job theme.
export interface SkillGroup {
  id: string;
  title: string;
  tags: string[];
  order?: number;
}

// Experience Bullet
export interface ExperienceBullet {
  text: string;
  tags: string[];
  include?: string;
}

// Experience Role
export interface ExperienceRole {
  title: string;
  startDate: string;
  endDate?: string;
  current: boolean;
  location?: string;
  bullets: ExperienceBullet[];
  include?: string;
}

// Experience Company
export interface ExperienceCompany {
  company: string;
  url: string[];
  employees?: string;
  startDate: string;
  endDate?: string;
  current: boolean;
  location?: string;
  description?: string;
  roles: ExperienceRole[];
  include?: string;
}

// Education Entry
export interface Education {
  title: string;
  org: string;
  url?: string;
  year: string;
  tags: string[];
  include?: string;
}

// Award Entry
export interface Award {
  title: string;
  issuer: string;
  reward: string;
  url?: string;
  year: string;
  tags: string[];
  include?: string;
}

// Presentation Entry (formerly Keynote)
export interface Presentation {
  title: string;
  event: string;
  url?: string;
  year: string;
  tags: string[];
  include?: string;
}

// Publication Entry
export interface Publication {
  title: string;
  url?: string;
  year: string;
  tags: string[];
  include?: string;
}

// Portfolio Entry
export interface Portfolio {
  title: string;
  url?: string;
  year: string;
  tags: string[];
  include?: string;
}

// Summary Phrase (for consistent summary generation)
export interface SummaryPhrase {
  text: string;
  tags: string[];
  include?: string;
}

// Synonym Set — words/phrases the user declares interchangeable in the
// context of this resume (e.g. "Solution Architect" // "Solution Engineer").
// `id` is the stable PK used to reference the set deterministically.
export interface SynonymSet {
  id: string;
  terms: string[];
}

// Demographics — optional EEO/voluntary-disclosure fields. These NEVER appear on
// a generated resume; they exist only to pre-fill application forms via the
// SkillSnap Chrome Extension. Values are stored as human-readable display strings
// so the extension can autofill them directly.
export interface Demographics {
  location_country?: string;           // default 'USA'
  authorized_to_work?: string;         // 'Yes' | 'No' | ''
  require_visa_sponsorship?: string;   // 'Yes' | 'No' | ''
  gender?: string;                     // 'Male' | 'Female' | 'Decline to Answer' | 'Other' | ''
  gender_other?: string;               // free text when gender === 'Other'
  race?: string;                       // one of RACE_OPTIONS | ''
  race_other?: string;                 // free text when race === 'Other'
  veteran?: string;                    // 'Yes' | 'No' | 'Decline to Answer' | 'Other' | ''
  veteran_other?: string;
  disabled?: string;                   // 'Yes' | 'No' | 'Decline to Answer' | 'Other' | ''
  disabled_other?: string;
}

// Recommendation from AI Resume Review
export interface Recommendation {
  id: string;
  path: string;
  field: string;
  desc: string;
  // 'advise' is informational only: data is null, it is never applied to the
  // resume (no Apply button, skipped by Apply All) — dismiss-only.
  action: 'replace' | 'append' | 'remove' | 'advise';
  data: unknown;
  // Which review pass produced this ('lint' | 'copyedit' | 'redundancy' |
  // 'credibility' | 'holistic'). Absent on legacy recommendations.
  category?: string;
  source?: string;
  // Legacy field for backwards compatibility with old recommendations
  replacement?: Record<string, unknown>;
  status: 'pending' | 'accepted' | 'dismissed';
  created_at: string;
}

// Per-resume section configuration, edited in the Edit Resume lightbox and
// stored on the resume at resumejson.section_settings. The backend resolves a
// section per-job as: per-job override > resume section_settings > legacy user
// preference (default_gen_* / section_names / section_order) > global default.
export interface SectionSettings {
  gen_types?: Partial<Record<SubcomponentType, GenerationType>>;
  names?: Partial<Record<SubcomponentType, string>>;   // custom display titles
  order?: SubcomponentType[];
}

// Resume JSON
export interface ResumeJSON {
  name: string;
  contact: ContactItem[];
  summary: string;
  skills: Skill[];
  skill_groups?: SkillGroup[];
  highlights: string[];
  experience: ExperienceCompany[];
  education: Education[];
  awards: Award[];
  presentations: Presentation[];
  publications: Publication[];
  portfolios: Portfolio[];
  summary_phrases?: SummaryPhrase[];
  synonyms?: SynonymSet[];
  // Optional demographics (EEO / voluntary disclosure). Never rendered on a
  // generated resume — used only to pre-fill applications via the Chrome Extension.
  demographics?: Demographics;
  recommendations?: Recommendation[];
  // Scorecard findings the user has manually dismissed ("Mark resolved"). Stored
  // by deterministic finding id; persists with the resume so dismissals stick.
  scorecard_resolved?: string[];
  // Per-resume generation types, custom section names and section order.
  section_settings?: SectionSettings;
}

// Resume
export interface Resume {
  userid: string;
  resumename: string;
  resumejson: ResumeJSON;
  lastupdate: string;
  master_resume_path?: string;
  publish_master_resume?: boolean;
  resume_tags?: string; // tag-generation status, 'generated' when complete
}

// Job
export interface Job {
  jobid: string;
  postedts: string;
  jobcompany: string;
  joblistid?: string;
  jobtitle: string;
  jobtitlesafe: string;
  jobdesc: string;
  joblocation?: string;
  jobsalary?: string;
  jobposteddate: string;
  joburl?: string;
  jobcompanylogo?: string;
  jobtags: string[];
  super_tags?: string[];  // high-level skill categories for job-tailored grouping
  jobreqid?: string;
  createdAt: string;
  // Junk-proof placeholder card: unsupported job board awaiting an auto-built
  // parser (WQ 4bda76f3). Never enters tag/resume generation until back-filled.
  placeholder?: boolean;
  placeholder_note?: string;
  // Terminal "research failed" state (WQ 15d32673): the card couldn't be
  // auto-researched after bounded retries — salary/location unavailable. Shown
  // with an open-posting link instead of the "in progress" spinner.
  research_failed?: boolean;
  research_note?: string;
}

// Response of POST /jobs/url — richer than a bare UserJob: the backend may
// return a placeholder card (unsupported board) or a list-import summary.
export interface CreateJobFromUrlResponse {
  message?: string;
  job?: Job;
  userJob?: UserJob;
  duplicate?: boolean;
  // A dormant duplicate (Declined/Expired/Skipped/Errored) that this re-add
  // brought back to the Queued phase.
  revived?: boolean;
  shared?: boolean;
  placeholder?: boolean;
  list_import?: { queued: number; dropped: number };
}

// User Job (combined with Job for display)
export interface UserJob extends Job {
  userid: string;
  resumeid?: string;
  jobphase: JobPhase;

  // Interview-question matches (populated when a card enters the Interviewing
  // phase — see shared/interview_match.py). Each is a matched question id + its
  // relevance score, sorted by score descending.
  interview_matches?: { qid: string; score: number }[];
  interview_matched_at?: string;
  // Client-only transient: 'matching' while the phase-change request that
  // computes the matches is in flight (server never persists this).
  interview_match_state?: 'matching' | 'done';

  // Candidate name (from resume)
  dataname?: string;
  // Subcomponent data
  datacontact?: string;
  datasummary?: string;
  dataskills?: string;
  datahighlights?: string;
  dataexperience?: string;
  dataeducation?: string;
  dataawards?: string;
  datapresentations?: string;
  datapublications?: string;
  dataportfolios?: string;
  datacoverletter?: string;
  dataelevatorpitch?: string;

  // Generation states
  statecontact: GenerationState;
  statesummary: GenerationState;
  stateskills: GenerationState;
  statehighlights: GenerationState;
  stateexperience: GenerationState;
  stateeducation: GenerationState;
  stateawards: GenerationState;
  statepresentations: GenerationState;
  statepublications: GenerationState;
  stateportfolios: GenerationState;
  statecoverletter: GenerationState;
  stateelevatorpitch: GenerationState;

  // Generation types
  typecontact: GenerationType;
  typesummary: GenerationType;
  typeskills: GenerationType;
  typehighlights: GenerationType;
  typeexperience: GenerationType;
  typeeducation: GenerationType;
  typeawards: GenerationType;
  typepresentations: GenerationType;
  typepublications: GenerationType;
  typeportfolios: GenerationType;
  typecoverletter: GenerationType;
  typeelevatorpitch: GenerationType;

  // Free-generation enablement (per user-job)
  is_enabled?: boolean;
  enabled_at?: string | null;

  // Final file locations
  s3locresumehtml?: string;
  s3locresumepdf?: string;
  s3loccoverletterhtml?: string;
  s3loccoverletterpdf?: string;
  s3locresumetxt?: string;
  s3loccoverlettertxt?: string;
  s3locresumetemplate?: string;

  // Feedback on generated files
  feedback_resume?: 'U' | 'D';
  feedback_coverletter?: 'U' | 'D';

  // Calculated fields
  postingAge?: number;
  updatedAt: string;

  // Phase-change tracking (Activity Report)
  phase_changed_at?: string;
  phase_history?: PhaseHistoryEntry[];
}

// One phase-change event on a job card. Appended by the backend on every
// jobphase transition (shared/dynamodb.update_user_job); legacy jobs that
// predate tracking may have no history at all.
export interface PhaseHistoryEntry {
  ts: string; // ISO timestamp
  phase: JobPhase | string;
}

// --- New orthogonal user model ---
// Role: permissions/access level
export type Role = 'user' | 'admin' | 'snappy' | 'test';

// Status: feature gating
export type Status = 'trial' | 'active' | 'hibernating' | 'cancelled' | 'expired';

// Plan: billing model
export type Plan = 'free' | 'consumer' | 'enterprise';


// User Preferences
// Abstract writing-voice fingerprint (closed-vocabulary enum traits only — never
// the raw sample, phrases, or facts). Written server-side by POST /writing-style,
// read-only on the client. Mirrors features/user_writing_style.md §2.
// One entry per corpus contributor measured into the fingerprint (v2). `chars`
// is an integer count only — never the text itself.
export interface WritingFingerprintSource {
  type: 'sample' | 'interview_qa' | 'application_qa' | 'resume_summary' | 'edited_output';
  chars: number;
}

// Integer-only style counters (v2). Nothing here is derivable back to content,
// which is why it is safe to persist. All fields optional for forward/back compat.
export interface WritingFingerprintStats {
  sentences?: number;
  words?: number;
  word_chars?: number;
  sent_len_sumsq?: number;
  em_dashes?: number;
  semicolons?: number;
  parentheticals?: number;
  exclamations?: number;
  contractions?: number;
  connective_counts?: Record<string, number>;
}

export interface WritingFingerprint {
  version: number; // 1 (legacy) or 2 (hybrid measured + judged)
  created_at: string;
  prompt_id: string;
  sample_chars: number;
  // v2 additions (absent on v1 fingerprints):
  sources?: WritingFingerprintSource[];
  stats?: WritingFingerprintStats;
  // traits may include the v2 `word_length` key ('short' | 'medium' | 'long').
  traits: Record<string, string | string[]>;
  // Traits the user has explicitly fine-tuned via calibration (§11.4).
  calibrated?: string[];
}

// Result of one calibration round — two rewrites of a canned paragraph the
// client shows side by side (order randomized). `current_value` is null when
// the trait is absent (the omit-when-unsure case). See §11.4.
export interface CalibrateRoundResult {
  trait: string;
  current_value: string | null;
  flipped_value: string;
  paragraph_id: string;
  text_current: string;
  text_flipped: string;
}

export interface UserPreferences {
  default_gen_contact: GenerationType;
  default_gen_summary: GenerationType;
  default_gen_skills: GenerationType;
  default_gen_highlights: GenerationType;
  default_gen_experience: GenerationType;
  default_gen_education: GenerationType;
  default_gen_awards: GenerationType;
  default_gen_presentations: GenerationType;
  default_gen_publications: GenerationType;
  default_gen_portfolios: GenerationType;
  default_gen_coverletter: GenerationType;
  // Resume Generation Options
  show_skillsnap_url?: boolean;
  show_year_education?: boolean;
  show_year_awards?: boolean;
  show_year_presentations?: boolean;
  show_year_publications?: boolean;
  show_year_portfolios?: boolean;
  cutoff_year?: number;
  target_pages?: number | null;
  bullet_period_style?: string;
  help_panel_open?: boolean;
  marketing_email_optin?: boolean;
  section_names?: string;
  section_order?: string;
  admin_timezone?: string;
  // App Options - Active Phases (Days before moving to Expired)
  days_queued_before_expired?: number;
  days_generating_before_expired?: number;
  days_ready_before_expired?: number;
  days_applied_before_expired?: number;
  days_followup_before_expired?: number;
  days_negotiation_before_expired?: number;
  days_accepted_before_expired?: number;
  days_declined_before_expired?: number;
  // App Options - Terminal Phases (Days before Trash/Delete)
  days_skipped_before_trashed?: number;
  days_errored_before_trashed?: number;
  days_expired_before_trashed?: number;
  days_trash_before_deleted?: number;
  // New orthogonal user model
  role?: Role;
  status?: Status;
  plan?: Plan;
  beta_tester?: boolean; // beta-tester program flag (missing == false)
  writing_fingerprint?: WritingFingerprint | null; // read-only; written by POST /writing-style
  email?: string; // login email — read-only, for display/verification only
  stripe_customer_id?: string;
  org_id?: string;
  org_name?: string;
  // Usage tracking
  jobs_created_this_month?: number;
  monthly_job_limit?: number;
  // Watermark & name link settings
  add_skillsnap_watermark?: boolean;
  link_name_to_url?: boolean;
  auto_download_files?: boolean;
  master_resume_format?: 'html' | 'pdf';
  // Per-user overrides (from admin)
  allowed_master_resumes?: number | null;
  allowed_job_cards_per_month?: number | null;
  // Free generation budget (trial + hibernating)
  free_gen_remaining?: number;
  free_gen_last_refill?: string;
  // Authorization-derived flags
  requires_watermark?: boolean;
  can_regenerate_section?: boolean;
}

// API Response Types
export interface ApiResponse<T> {
  data?: T;
  error?: string;
  message?: string;
}

export interface JobListResponse {
  jobs: UserJob[];
  count: number;
  // Present on the first page of a paged (limit=N) aggregate fetch and on all
  // legacy full fetches; absent on cursor-continuation pages.
  phaseCounts: Record<JobPhase, number>;
  totalCount: number;
  // Paged fetches only: opaque next-page token (null/absent when exhausted).
  cursor?: string | null;
}

export interface ResumeListResponse {
  resumes: Resume[];
  count: number;
}

export interface GenerationStatusResponse {
  jobid: string;
  phase: JobPhase;
  overallStatus: 'ready' | 'generating' | 'complete' | 'error';
  subcomponents: Record<SubcomponentType, {
    state: GenerationState;
    type: GenerationType;
    hasContent: boolean;
  }>;
  finalFiles: {
    resumeHtml: { ready: boolean; generated: boolean; s3loc: string };
    resumePdf: { ready: boolean; generated: boolean; s3loc: string };
    coverLetterHtml: { ready: boolean; generated: boolean; s3loc: string };
    coverLetterPdf: { ready: boolean; generated: boolean; s3loc: string };
  };
  allComplete: boolean;
}

// User
export interface User {
  userid: string;
  userhandle: string;
  email: string;
  username: string;
}

// Application Answers (user-authored Q&A for application forms)
export interface ApplicationAnswer {
  userid: string;
  qaid: string;
  question: string;
  answer: string;
  recommendations: string;
  linked_jobids: string[];
  created_at: string;
  updated_at: string;
  cloned_from?: string;
}

export interface ApplicationAnswersListResponse {
  answers: ApplicationAnswer[];
  count: number;
  trial: boolean;
  trial_max: number;
}

// Interview Q&A (shared question library + per-user practice data)
export interface InterviewQuestion {
  userid: string; // 'public' for the shared library, else the owner's uuid
  qid: string;
  question: string;
  tags: string[];
  source?: string; // 'seed' | 'user'
  created_at?: string;
  updated_at?: string;
}

export interface InterviewUserData {
  starred: boolean;
  answer: string;
  recommendations: string;
  updated_at?: string;
}

export interface InterviewListResponse {
  questions: InterviewQuestion[];
  user_data: Record<string, InterviewUserData>;
  count: number;
  active: boolean; // status=='active' — gates the Get Feedback button
}

// Import Types
export interface ImportUrlResponse {
  uploadUrl: string;
  s3Key: string;
  expiresIn: number;
}

export interface ImportProcessResponse {
  resumejson: ResumeJSON;
  warnings?: string[];
  source: 'yaml' | 'json' | 'pdf';
}

// Organization Types
export interface Organization {
  org_id: string;
  org_name: string;
  billing_contact?: string;
  billing_email?: string;
  renewal_date?: string;
  member_count?: number;
  status?: 'active' | 'inactive' | 'suspended';
  created_at?: string;
  updated_at?: string;
}

export interface JoinOrganizationResponse {
  message: string;
  organization: {
    org_id: string;
    org_name: string;
  };
}
